import { PageHeader } from "../components/PageHeader";
import { CTASection } from "../components/CTASection";
import { Reveal } from "../components/Reveal";
import { IMAGES, TIMELINE, ORG } from "../data/content";
import { Target, Eye, HeartHandshake, CheckCircle2 } from "lucide-react";

const OBJECTIVES = [
  "Improve healthcare awareness and access in rural and underserved communities",
  "Organize free health check-up and eye screening camps across Assam",
  "Promote education, hygiene and preventive healthcare in schools",
  "Empower women through skill development and self-help initiatives",
  "Support persons with disabilities through training and inclusion programs",
  "Conduct awareness campaigns on HIV/AIDS, cancer, diabetes and tobacco",
];

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHeader
        title="About Us"
        subtitle="Learn about our journey, our values and the people behind Helpline Health Care Society."
      />

      <section className="py-16 sm:py-20 bg-white">
        <div className="container-hecso grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-widest text-hecso-orange">Who We Are</span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-hecso-ink">
              Serving the people of Assam since 18 April 2004
            </h2>
            <p className="mt-5 text-sm sm:text-base text-hecso-muted leading-relaxed">
              Helpline Health Care Society (HECSO) was established on {ORG.established} with the objective of
              improving healthcare awareness, education and social welfare within communities across Assam. What
              began as a small group of healthcare workers and volunteers in Guwahati has grown into a trusted
              community organization working across 15+ districts.
            </p>
            <p className="mt-4 text-sm sm:text-base text-hecso-muted leading-relaxed">
              Over the years the organization has conducted health camps, eye screening programs, awareness
              campaigns, educational initiatives, disability support programs and community development projects —
              always in close partnership with local communities, schools, health departments and institutions.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <img src={IMAGES.ruralCommunity} alt="Community gathering supported by HECSO" className="rounded-2xl w-full h-[420px] object-cover shadow-md" />
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-hecso-beige" data-testid="mission-section">
        <div className="container-hecso grid md:grid-cols-3 gap-6">
          {[
            { icon: Target, title: "Our Mission", text: "To improve the health, education and well-being of underserved communities in Assam through accessible, compassionate and sustainable programs." },
            { icon: Eye, title: "Our Vision", text: "Healthy, aware and empowered communities across Assam, where every family has access to essential healthcare, education and opportunity." },
            { icon: HeartHandshake, title: "Our Values", text: "Compassion, transparency, community participation and accountability guide every camp, program and partnership we undertake." },
          ].map((v, i) => (
            <Reveal key={v.title} delay={i * 100}>
              <div className="h-full rounded-xl border border-slate-200 bg-white p-7 shadow-sm" data-testid={`value-card-${i}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-hecso-greensubtle text-hecso-green">
                  <v.icon className="w-6 h-6" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-hecso-ink">{v.title}</h3>
                <p className="mt-2 text-sm text-hecso-muted leading-relaxed">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white" data-testid="objectives-section">
        <div className="container-hecso grid lg:grid-cols-2 gap-12">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-hecso-ink">Our Objectives</h2>
            <ul className="mt-6 space-y-4">
              {OBJECTIVES.map((o, i) => (
                <li key={i} className="flex gap-3 text-sm sm:text-base text-hecso-muted leading-relaxed">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-hecso-green" /> {o}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={150}>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-hecso-ink">Our Journey</h2>
            <div className="mt-6 space-y-0" data-testid="timeline">
              {TIMELINE.map((t, i) => (
                <div key={t.year} className="relative flex gap-5 pb-8 last:pb-0">
                  {i < TIMELINE.length - 1 && <span className="absolute left-[26px] top-8 bottom-0 w-px bg-emerald-200" />}
                  <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full bg-hecso-green font-heading text-sm font-bold text-white">
                    {t.year}
                  </span>
                  <p className="pt-2 text-sm text-hecso-muted leading-relaxed">{t.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
